import { currency, EmptyRow, percent, signed } from "../App";
import type {
  BreakdownRow,
  CostDimension,
  CostFilters,
  DashboardResponse,
  DriverGroup,
} from "../../shared/contracts";
import { toCsv, type CsvCell } from "../csv";
import { ExpandIcon } from "../icons";

export const dimensions: Array<{ value: CostDimension; label: string }> = [
  { value: "service", label: "Service" },
  { value: "linkedAccount", label: "Linked account" },
  { value: "region", label: "Region" },
  { value: "usageType", label: "Usage type" },
  { value: "tag", label: "Tag" },
];

export function driversCsv(group: DriverGroup): string {
  return toCsv(
    [
      group.label,
      "Cost",
      "Amortized",
      "Previous",
      "Change",
      "Change %",
      "Share %",
      "Movement",
    ],
    group.rows.map((row): CsvCell[] => [
      row.label,
      row.cost,
      row.amortizedCost,
      row.previousCost,
      row.change,
      row.changePercent === null ? "" : Number(row.changePercent.toFixed(2)),
      Number((row.share * 100).toFixed(2)),
      row.movement,
    ]),
  );
}

export function MovementBadge({ row }: { row: BreakdownRow }) {
  if (row.movement === "new")
    return <span className="badge badge--pending">New</span>;
  if (row.movement === "removed") return <span className="badge">Stopped</span>;
  return null;
}

function driverAsk(
  row: BreakdownRow,
  dimensionLabel: string,
  currencyCode: string,
): string {
  return (
    `Cost driver (${dimensionLabel}) — ${row.label}: cost ${currency(row.cost, currencyCode)}, ` +
    `previous ${currency(row.previousCost, currencyCode)}, change ${signed(row.change, currencyCode)} ` +
    `(${percent(row.changePercent)}), share ${(row.share * 100).toFixed(1)}%`
  );
}

export function DriversTable({
  breakdown,
  dimensionLabel,
  currencyCode,
  variant,
}: {
  breakdown: BreakdownRow[];
  dimensionLabel: string;
  currencyCode: string;
  variant: "inline" | "full";
}) {
  const full = variant === "full";
  return (
    <div className="table-wrap">
      <table>
        <caption className="sr-only">
          Period-over-period change by {dimensionLabel}
        </caption>
        <thead>
          <tr>
            <th>{dimensionLabel}</th>
            <th>Cost</th>
            {full && <th>Amortized</th>}
            <th>Previous</th>
            <th>Change</th>
            <th>Change %</th>
            <th>Share</th>
          </tr>
        </thead>
        <tbody>
          {breakdown.length === 0 ? (
            <EmptyRow
              span={full ? 7 : 6}
              message="No cost drivers for this range yet."
            />
          ) : (
            breakdown.map((row) => (
              <tr
                key={row.key}
                data-ask={driverAsk(row, dimensionLabel, currencyCode)}
              >
                <td className="wrap">
                  {row.label} <MovementBadge row={row} />
                </td>
                <td className="number">{currency(row.cost, currencyCode)}</td>
                {full && (
                  <td className="number">
                    {currency(row.amortizedCost, currencyCode)}
                  </td>
                )}
                <td className="number">
                  {currency(row.previousCost, currencyCode)}
                </td>
                <td
                  className={
                    row.change > 0 ? "number change-up" : "number change-down"
                  }
                >
                  {signed(row.change, currencyCode)}
                </td>
                <td
                  className={
                    row.change > 0 ? "number change-up" : "number change-down"
                  }
                >
                  {percent(row.changePercent)}
                </td>
                <td className="number">{(row.share * 100).toFixed(1)}%</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export function DriversCard({
  data,
  filters,
  groupBy,
  onSelectDimension,
  onExport,
  onExpand,
}: {
  data: DashboardResponse;
  filters: CostFilters;
  groupBy: CostDimension;
  onSelectDimension: (dimension: CostDimension) => void;
  onExport: () => void;
  onExpand: () => void;
}) {
  const selectedGroup = data.drivers.find(
    (group) => group.dimension === groupBy,
  );
  const dimensionLabel =
    dimensions.find((item) => item.value === groupBy)?.label ?? "dimension";

  return (
    <section className="card" id="drivers" aria-labelledby="drivers-heading">
      <div
        className="card__header card__header--wrap"
        data-ask={`Cost drivers section — ${data.breakdown.length} rows shown`}
      >
        <div>
          <h2 id="drivers-heading">Cost drivers</h2>
          <p>
            What moved spend against the preceding{" "}
            {Math.round(
              (Date.parse(`${filters.end}T00:00:00Z`) -
                Date.parse(`${filters.start}T00:00:00Z`)) /
                86_400_000,
            )}
            -day period, ranked by absolute change.
          </p>
        </div>
        <div className="card__header-actions">
          <div
            className="segmented"
            role="group"
            aria-label="Breakdown dimension"
          >
            {dimensions.map((dimension) => (
              <button
                key={dimension.value}
                type="button"
                onClick={() => onSelectDimension(dimension.value)}
                aria-pressed={groupBy === dimension.value}
              >
                {dimension.label}
              </button>
            ))}
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onExpand}
            aria-label="Expand Cost drivers table"
            title="Expand"
          >
            <ExpandIcon size={16} />
          </button>
        </div>
      </div>

      <ul className="driver-highlights">
        {data.drivers
          .filter((group) => group.label !== "")
          .map((group) => {
            const mover = group.rows[0];
            return (
              <li
                key={group.dimension}
                data-ask={
                  mover
                    ? `Top mover (${group.label}) — ${mover.label}: ${signed(mover.change, data.currency)} (${percent(mover.changePercent)})`
                    : undefined
                }
              >
                <p className="eyebrow">{group.label}</p>
                {mover ? (
                  <>
                    <p className="driver-highlights__mover">{mover.label}</p>
                    <p
                      className={mover.change > 0 ? "change-up" : "change-down"}
                    >
                      {signed(mover.change, data.currency)} ·{" "}
                      {percent(mover.changePercent)}
                    </p>
                  </>
                ) : (
                  <p className="muted">No movement</p>
                )}
              </li>
            );
          })}
      </ul>

      <DriversTable
        breakdown={data.breakdown}
        dimensionLabel={dimensionLabel}
        currencyCode={data.currency}
        variant="inline"
      />
      <footer className="card__footer card__footer--split">
        <span>
          Net change across these rows:{" "}
          {signed(selectedGroup?.netChange ?? 0, data.currency)}
        </span>
        <button
          className="button button--ghost button--small"
          type="button"
          onClick={onExport}
          disabled={data.breakdown.length === 0}
        >
          Export CSV
        </button>
      </footer>
    </section>
  );
}
